import { create } from 'zustand';

export type BugReportTab = 'list' | 'stats';

/** 데스크톱은 타이틀바 슬롯이, 모바일은 본문이 그리므로 라벨은 여기 한 벌만 둔다. */
export const BUG_REPORT_VIEW_TABS: ReadonlyArray<{ id: BugReportTab; label: string }> = [
  { id: 'list', label: '목록' },
  { id: 'stats', label: '통계' },
];

/** 모바일 상세가 쌓은 방문 기록 칸의 표식. 이 키가 있는 칸만 우리가 되돌린다. */
export const MOBILE_DETAIL_HISTORY_KEY = 'bugReportMobileDetail';

export function isOurHistoryEntry(): boolean {
  const state: unknown = window.history.state;
  return (
    typeof state === 'object' &&
    state !== null &&
    (state as Record<string, unknown>)[MOBILE_DETAIL_HISTORY_KEY] === true
  );
}

interface BugReportViewState {
  activeTab: BugReportTab;
  setActiveTab: (tab: BugReportTab) => void;
  /** 모바일에서 상세가 목록을 덮고 있는가. 데스크톱은 목록·상세가 나란하므로 쓰지 않는다. */
  mobileDetailOpen: boolean;
  /** 상세를 열고 방문 기록 한 칸을 쌓는다 — 기기 뒤로 가기가 목록으로 돌아오게. */
  openMobileDetail: () => void;
  /** 상세를 닫는다. 우리 칸이 남아 있으면 history.back() 으로 되돌려 popstate 가 닫게 한다. */
  closeMobileDetail: () => void;
  /** popstate 뒤 현재 기록 칸에 맞춰 상태를 맞춘다(앞으로 가기로 돌아오면 다시 연다). */
  syncMobileDetailFromHistory: () => void;
}

/**
 * 뷰 전환(목록/통계) 채널 — 창 크롬의 titleSlot 과 본문(`BugReportPage`)을 잇는다.
 *
 * 토글은 목록 뷰와 통계 뷰의 **공통 조상**에만 있을 수 있다. 목록 패널 안에 두면 통계를
 * 켜는 순간 패널째 `BugReportStats` 로 교체되면서 토글도 사라져 되돌아올 길이 없고,
 * 통계를 상세 패널로 보내는 것도 안 된다 — `BugReportStats` 는 창 폭 전체를 전제한다.
 * 남는 자리가 타이틀바이고, 그 슬롯은 `windowContent` 바깥이라 컨텍스트가 닿지 않는다.
 *
 * 패턴 선례: `useTodayHistoryStore` — footer band(shell 바깥)와 패널 소유자(안)를 잇는
 * 같은 모양의 경량 채널이다. 여기서도 렌더 소유권은 그대로 `BugReportPage` 에 있고,
 * 이 store 는 "어느 뷰인지" 한 축만 나른다.
 *
 * 모바일 상세 열림(`mobileDetailOpen`)도 같은 이유로 여기 있다. 상세에서 목록으로 돌아가는
 * 버튼은 창 크롬의 제목줄(`MobileWindowTakeover` leadingSlot)에 서므로 본문 state 가
 * 닿지 않는다. 버튼·기기 뒤로 가기·스와이프가 모두 history 한 경로로 닫히게 한다.
 */
export const useBugReportViewStore = create<BugReportViewState>((set, get) => ({
  activeTab: 'list',
  setActiveTab: (activeTab) => set({ activeTab }),
  mobileDetailOpen: false,
  openMobileDetail: () => {
    if (get().mobileDetailOpen) return;
    const prev: unknown = window.history.state;
    const base = typeof prev === 'object' && prev !== null ? prev : {};
    window.history.pushState({ ...base, [MOBILE_DETAIL_HISTORY_KEY]: true }, '');
    set({ mobileDetailOpen: true });
  },
  closeMobileDetail: () => {
    if (!get().mobileDetailOpen) return;
    if (isOurHistoryEntry()) {
      window.history.back();
      return;
    }
    set({ mobileDetailOpen: false });
  },
  syncMobileDetailFromHistory: () => set({ mobileDetailOpen: isOurHistoryEntry() }),
}));
